export const OPS_BY_FAMILY = {
  string: [
    'eq',
    'neq',
    'in',
    'not_in',
    'contains',
    'not_contains',
    'starts_with',
    'ends_with',
    'is_empty',
    'is_not_empty',
    'is_null',
    'is_not_null',
  ],
  numeric: ['eq', 'neq', 'in', 'not_in', 'lt', 'lte', 'gt', 'gte', 'between', 'is_null', 'is_not_null'],
  date: ['eq', 'neq', 'lt', 'lte', 'gt', 'gte', 'between', 'in_last', 'is_null', 'is_not_null'],
  timestamp: ['eq', 'neq', 'lt', 'lte', 'gt', 'gte', 'between', 'in_last', 'is_null', 'is_not_null'],
  boolean: ['is_null', 'is_not_null', 'is_true', 'is_not_true'],
  array: ['array_contains', 'in', 'not_in', 'is_null', 'is_not_null'],
};

/** Plain-language operator labels, keyed by type family (falls back to `default`). */
export const OP_LABELS_BY_FAMILY = {
  default: {
    eq: 'is',
    neq: 'is not',
    in: 'is any of',
    not_in: 'is not any of',
    lt: 'is less than',
    lte: 'is at most',
    gt: 'is more than',
    gte: 'is at least',
    between: 'is between',
    in_last: 'is in the last',
    contains: 'contains',
    not_contains: 'does not contain',
    starts_with: 'starts with',
    ends_with: 'ends with',
    is_empty: 'is empty',
    is_not_empty: 'is not empty',
    is_null: 'is missing',
    is_not_null: 'is present',
    is_true: 'is true',
    is_not_true: 'is not true',
    array_contains: 'includes',
  },
  date: {
    eq: 'is on',
    neq: 'is not on',
    lt: 'is before',
    lte: 'is on or before',
    gt: 'is after',
    gte: 'is on or after',
  },
  timestamp: {
    eq: 'is at',
    neq: 'is not at',
    lt: 'is before',
    lte: 'is at or before',
    gt: 'is after',
    gte: 'is at or after',
  },
  array: {
    eq: 'includes',
    neq: 'does not include',
    in: 'includes any of',
    not_in: 'includes none of',
  },
};

/** Flat label map (legacy name) — default-family labels. */
export const OP_LABELS = OP_LABELS_BY_FAMILY.default;

export function opLabel(op, columnType) {
  const fam = typeFamily(columnType);
  const famLabels = OP_LABELS_BY_FAMILY[fam] || {};
  return famLabels[op] || OP_LABELS_BY_FAMILY.default[op] || op;
}

/** Short symbolic label for compact summaries. */
export const OP_SYMBOLS = {
  eq: '=',
  neq: '≠',
  lt: '<',
  lte: '≤',
  gt: '>',
  gte: '≥',
};

export const RELATIVE_DATE_UNITS = [
  { value: 'days', label: 'days' },
  { value: 'months', label: 'months' },
  { value: 'years', label: 'years' },
];

export function typeFamily(columnType) {
  const t = String(columnType || '').trim().toLowerCase();
  if (!t) return 'string';
  if (t.startsWith('array')) return 'array';
  if (t === 'boolean' || t === 'bool') return 'boolean';
  if (t === 'date') return 'date';
  if (t === 'timestamp' || t === 'timestamptz') return 'timestamp';
  if (
    ['bigint', 'int', 'integer', 'smallint', 'tinyint', 'float', 'double', 'decimal', 'numeric', 'long'].includes(t)
  ) {
    return 'numeric';
  }
  return 'string';
}

export const OPS_FOR_VALUE_LIST = ['in', 'not_in', 'eq', 'neq', 'is_null', 'is_not_null'];

/** Operators that stay meaningful for the derived fast_id_score column (0–10 score). */
export const OPS_FOR_FAST_ID_SCORE = ['eq', 'neq', 'gte', 'gt', 'lte', 'lt', 'between', 'is_null', 'is_not_null'];

export function isFastIdScoreColumn(condition) {
  return (
    String(condition?.column || '') === 'fast_id_score' &&
    ((condition?.schema === 'profile' && condition?.table === 'profile') ||
      (condition?.schema === 'signals' && condition?.table === 'last_signals_gold'))
  );
}

export function operatorsForType(columnType) {
  const fam = typeFamily(columnType);
  return OPS_BY_FAMILY[fam] || OPS_BY_FAMILY.string;
}

export function operatorsForColumn(columnType, hasValueList, condition = null) {
  if (condition && isFastIdScoreColumn(condition)) return OPS_FOR_FAST_ID_SCORE;
  if (hasValueList) return OPS_FOR_VALUE_LIST;
  return operatorsForType(columnType);
}

/** Default operator when a column is first chosen. */
export function defaultOperatorForColumn(col) {
  if (col && isFastIdScoreColumn(col)) return 'gte';
  if (col?.has_value_list) return 'in';
  const fam = typeFamily(col?.column_type);
  if (fam === 'date' || fam === 'timestamp') return 'in_last';
  if (fam === 'boolean') return 'is_true';
  if (fam === 'array') return 'array_contains';
  return 'eq';
}

export function opNeedsNoValue(op) {
  return (
    op === 'is_null' ||
    op === 'is_not_null' ||
    op === 'is_true' ||
    op === 'is_not_true' ||
    op === 'is_empty' ||
    op === 'is_not_empty'
  );
}

export function opIsMulti(op) {
  return op === 'in' || op === 'not_in';
}

/** Parse a stored multi-value operand (JSON array preferred; comma/newline fallback). */
export function parseListValue(raw) {
  let parts;
  if (Array.isArray(raw)) {
    parts = raw;
  } else {
    const s = String(raw ?? '').trim();
    if (!s) return [];
    parts = null;
    if (s.startsWith('[')) {
      try {
        const parsed = JSON.parse(s);
        if (Array.isArray(parsed)) parts = parsed;
      } catch (e) {
        /* fall through to delimiter parsing */
      }
    }
    if (!parts) parts = s.includes('\n') ? s.split('\n') : s.split(',');
  }
  const out = [];
  for (const part of parts) {
    const v = String(part ?? '').trim();
    if (v && !out.includes(v)) out.push(v);
  }
  return out;
}

/**
 * Normalize a `/column-values` response into `[{ value, label }]`, where `label` is
 * `value_label` when the dictionary provides one and the raw value otherwise.
 */
export function normalizeColumnValues(data) {
  if (!Array.isArray(data)) return [];
  const out = [];
  for (const entry of data) {
    const isObject = entry && typeof entry === 'object';
    const value = String((isObject ? entry.value : entry) ?? '').trim();
    if (!value) continue;
    const label = String((isObject ? entry.value_label : '') ?? '').trim();
    out.push({ value, label: label || value });
  }
  return out;
}

/** Display text for a stored value: its label from the column's value list, else the value itself. */
export function columnValueLabel(value, options) {
  const match = (options || []).find((o) => o.value === value);
  return match ? match.label : String(value ?? '');
}

export function serializeListValue(values) {
  const list = (values || []).map((v) => String(v).trim()).filter(Boolean);
  return list.length ? JSON.stringify(list) : '';
}

export function opIsInLast(op) {
  return op === 'in_last';
}

/** True when an SQL-preview error should be shown under Operator/Value instead of globally. */
export function isConditionValueError(message) {
  const msg = String(message || '');
  return (
    /value is required/i.test(msg) ||
    /requires a (non-empty )?value/i.test(msg) ||
    /requires two values/i.test(msg) ||
    /date or timestamp value is required/i.test(msg) ||
    /In the last/i.test(msg) ||
    /Invalid numeric literal/i.test(msg) ||
    /array_contains requires a value/i.test(msg)
  );
}

/**
 * Field-level value errors for a condition (mirrors segment SQL generation rules).
 * Returns `{ value?, value2? }` message strings when the condition is complete enough to validate.
 */
export function conditionFieldErrors(condition) {
  const errors = {};
  if (!condition?.catalog || !condition?.schema || !condition?.table || !condition?.column) {
    return errors;
  }

  const op = String(condition.operator || '').trim().toLowerCase();
  if (opNeedsNoValue(op)) return errors;

  const fam = typeFamily(condition.column_type);
  const v = String(condition.value ?? '').trim();
  const v2 = String(condition.value2 ?? '').trim();

  if (opIsMulti(op)) {
    const list = parseListValue(condition.value);
    if (!list.length) {
      errors.value = 'Choose at least one value.';
    } else if (fam === 'numeric') {
      const bad = list.find((x) => !/^-?\d+(\.\d+)?([eE][+-]?\d+)?$/.test(x));
      if (bad) errors.value = `Invalid numeric literal: '${bad}'`;
    }
    return errors;
  }

  if (isFastIdScoreColumn(condition)) {
    const okScore = (x) => /^\d+$/.test(x) && Number(x) >= 0 && Number(x) <= 10;
    if (!v || !okScore(v)) errors.value = 'Fast-ID score must be a whole number from 0 to 10.';
    if (op === 'between' && (!v2 || !okScore(v2))) errors.value2 = 'Fast-ID score must be a whole number from 0 to 10.';
    return errors;
  }

  if (op === 'between') {
    if (!v) errors.value = 'A value is required for this operator.';
    if (!v2) errors.value2 = 'A value is required for this operator.';
    if (v && fam === 'numeric' && !/^-?\d+(\.\d+)?([eE][+-]?\d+)?$/.test(v)) {
      errors.value = `Invalid numeric literal: '${condition.value}'`;
    }
    if (v2 && fam === 'numeric' && !/^-?\d+(\.\d+)?([eE][+-]?\d+)?$/.test(v2)) {
      errors.value2 = `Invalid numeric literal: '${condition.value2}'`;
    }
    return errors;
  }

  if (opIsInLast(op)) {
    if (!/^\d+$/.test(v) || Number(v) < 1) {
      errors.value = '"In the last" requires a positive whole number (e.g. 30).';
    } else if (Number(v) > 99999) {
      errors.value = '"In the last" amount is too large.';
    }
    return errors;
  }

  if (fam === 'numeric') {
    if (!v) {
      errors.value = 'A value is required for this operator.';
    } else if (!/^-?\d+(\.\d+)?([eE][+-]?\d+)?$/.test(v)) {
      errors.value = `Invalid numeric literal: '${condition.value}'`;
    }
    return errors;
  }

  if (fam === 'date' || fam === 'timestamp') {
    if (!v) errors.value = 'A date or timestamp value is required.';
    return errors;
  }

  if (fam === 'array' && op === 'array_contains') {
    if (!v) errors.value = 'array_contains requires a value.';
    return errors;
  }

  if (['contains', 'not_contains', 'starts_with', 'ends_with'].includes(op)) {
    if (!v) errors.value = `Operator '${op}' requires a non-empty value.`;
    return errors;
  }

  // Plain string comparisons: an empty value is almost always a half-finished row.
  // Use "is empty" / "is not empty" to match blanks on purpose.
  if ((op === 'eq' || op === 'neq') && !v) {
    errors.value = 'Enter a value, or use “is empty”.';
  }

  return errors;
}

export function emptyCondition() {
  return {
    kind: 'condition',
    catalog: '',
    schema: '',
    table: '',
    column: '',
    column_type: '',
    has_value_list: false,
    display_name: '',
    operator: 'eq',
    value: '',
    value2: '',
    folder_list: [],
  };
}

export function emptyGroup() {
  return { kind: 'group', op: 'AND', children: [] };
}

export function defaultCriteriaRoot() {
  return { kind: 'group', op: 'AND', children: [] };
}

/** True when criteria includes at least one condition with catalog/schema/table set. */
export function criteriaHasTableReferences(root) {
  if (!root) return false;
  if (root.kind === 'condition') {
    return Boolean(root.catalog && root.schema && root.table);
  }
  if (root.kind === 'group') {
    return (root.children || []).some(criteriaHasTableReferences);
  }
  return false;
}

export function deepClone(obj) {
  return JSON.parse(JSON.stringify(obj));
}

export function getNode(root, path) {
  let n = root;
  for (let i = 0; i < path.length; i += 1) {
    if (!n || n.kind !== 'group') return null;
    n = n.children[path[i]];
  }
  return n;
}

export function updateNodeAtPath(root, path, updater) {
  if (path.length === 0) {
    return updater(deepClone(root));
  }
  const next = deepClone(root);
  let parent = next;
  for (let i = 0; i < path.length - 1; i += 1) {
    parent = parent.children[path[i]];
  }
  const idx = path[path.length - 1];
  parent.children[idx] = updater(parent.children[idx]);
  return next;
}

export function removeAtPath(root, path) {
  if (path.length === 0) return root;
  const next = deepClone(root);
  const parentPath = path.slice(0, -1);
  const idx = path[path.length - 1];
  let parent = next;
  for (let i = 0; i < parentPath.length; i += 1) {
    parent = parent.children[parentPath[i]];
  }
  parent.children.splice(idx, 1);
  return next;
}

export function appendChildAtPath(root, path, child) {
  return updateNodeAtPath(root, path, (node) => ({
    ...node,
    children: [...(node.children || []), child],
  }));
}

/** Insert `child` into the group at `parentPath` at `index`. */
export function insertChildAtPath(root, parentPath, index, child) {
  return updateNodeAtPath(root, parentPath, (node) => {
    const children = [...(node.children || [])];
    const i = Math.max(0, Math.min(index, children.length));
    children.splice(i, 0, child);
    return { ...node, children };
  });
}

/** Duplicate the node at `path`, placing the copy right after it. */
export function duplicateAtPath(root, path) {
  if (!path.length) return root;
  const node = getNode(root, path);
  if (!node) return root;
  const parentPath = path.slice(0, -1);
  const idx = path[path.length - 1];
  return insertChildAtPath(root, parentPath, idx + 1, deepClone(node));
}

/** Wrap the node at `path` in a new group (so it can be combined with siblings using the other operator). */
export function wrapInGroupAtPath(root, path, op = 'OR') {
  if (!path.length) return root;
  return updateNodeAtPath(root, path, (node) => ({ kind: 'group', op, children: [node] }));
}

/** True when the node at `path` sits inside a nested group and can be lifted out of it. */
export function canMoveOutOfGroup(path) {
  return Array.isArray(path) && path.length > 1;
}

/** Lift the node at `path` out of its group, placing it just after that group in the level above. */
export function moveOutOfGroupAtPath(root, path) {
  if (!canMoveOutOfGroup(path)) return root;
  const node = getNode(root, path);
  if (!node) return root;
  const parentPath = path.slice(0, -1);
  const grandParentPath = parentPath.slice(0, -1);
  const parentIdx = parentPath[parentPath.length - 1];

  let next = removeAtPath(root, path);
  const parent = getNode(next, parentPath);
  // The backend rejects empty groups, so a group whose last child just left is dropped
  // and the node takes its place.
  if (parent && !(parent.children || []).length) {
    next = removeAtPath(next, parentPath);
    return insertChildAtPath(next, grandParentPath, parentIdx, deepClone(node));
  }
  return insertChildAtPath(next, grandParentPath, parentIdx + 1, deepClone(node));
}

/** Move a child within its parent group by `delta` (-1 up, +1 down). */
export function moveWithinParent(root, path, delta) {
  if (!path.length) return root;
  const parentPath = path.slice(0, -1);
  const idx = path[path.length - 1];
  return updateNodeAtPath(root, parentPath, (node) => {
    const children = [...(node.children || [])];
    const to = idx + delta;
    if (to < 0 || to >= children.length) return node;
    const [item] = children.splice(idx, 1);
    children.splice(to, 0, item);
    return { ...node, children };
  });
}

/** True when a condition has a column picked. */
export function conditionHasColumn(condition) {
  return Boolean(condition?.catalog && condition?.schema && condition?.table && condition?.column);
}

/**
 * Whether the whole tree is ready to preview/count: at least one condition,
 * every condition has a column, and no field-level errors.
 */
export function criteriaIsComplete(root) {
  let count = 0;
  let ok = true;
  const walk = (n) => {
    if (!n || !ok) return;
    if (n.kind === 'condition') {
      count += 1;
      if (!conditionHasColumn(n)) {
        ok = false;
        return;
      }
      const errs = conditionFieldErrors(n);
      if (Object.keys(errs).length) ok = false;
      return;
    }
    // The backend rejects empty nested groups; the root may be empty only while count stays 0.
    if (!(n.children || []).length && n !== root) {
      ok = false;
      return;
    }
    (n.children || []).forEach(walk);
  };
  walk(root);
  return ok && count > 0;
}

/** Human-readable reason the tree is not ready, or '' when it is. */
export function criteriaIncompleteReason(root) {
  let reason = '';
  let count = 0;
  const walk = (n) => {
    if (!n || reason) return;
    if (n.kind === 'condition') {
      count += 1;
      if (!conditionHasColumn(n)) reason = 'Choose a column for every condition.';
      else {
        const errs = conditionFieldErrors(n);
        const first = errs.value || errs.value2;
        if (first) reason = first;
      }
      return;
    }
    if (!(n.children || []).length && n !== root) {
      reason = 'Remove empty groups or add a condition to them.';
      return;
    }
    (n.children || []).forEach(walk);
  };
  walk(root);
  if (!reason && count === 0) reason = 'Add at least one condition.';
  return reason;
}

/** Pick the column's human label from a condition or a search hit. */
export function columnLabel(colOrCondition) {
  return (
    colOrCondition?.display_name ||
    colOrCondition?.column_name ||
    colOrCondition?.column ||
    ''
  );
}

export function conditionDisplayLabel(condition) {
  if (condition.catalog && condition.schema && condition.table && condition.column) {
    return [condition.catalog, condition.schema, condition.table, condition.column].join('.');
  }
  return '';
}

const FRIENDLY_TABLE_DROP_TOKENS = new Set(['md5', 'flattened', 'last', 'gold']);

/** Turn a raw table name into a short Title Case label for typeahead UI. */
export function friendlyTableName(tableName) {
  const raw = String(tableName || '').trim();
  if (!raw) return '';
  return raw
    .split(/[_-]+/)
    .filter((token) => token && !FRIENDLY_TABLE_DROP_TOKENS.has(token.toLowerCase()))
    .map((token) => token.charAt(0).toUpperCase() + token.slice(1))
    .join(' ');
}

/** Base typeahead label: display name (or column name). */
export function columnOptionBaseLabel(hit) {
  return String(hit?.display_name || hit?.column_name || hit?.column || '—');
}

/**
 * Labels for a list of column hits. When multiple hits share the same base label
 * (e.g. two columns both named Domain on Profile), append the raw column name.
 */
export function columnOptionLabels(hits) {
  const list = Array.isArray(hits) ? hits : [];
  const bases = list.map(columnOptionBaseLabel);
  const counts = new Map();
  for (const base of bases) {
    counts.set(base, (counts.get(base) || 0) + 1);
  }
  return list.map((hit, i) => {
    const base = bases[i];
    if ((counts.get(base) || 0) < 2) return base;
    const col = String(hit?.column || '').trim();
    return col ? `${base} · ${col}` : base;
  });
}
